/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { ReactNode, createElement } from "react";

// @mui material components
import Box from "@mui/material/Box";

// lucide icons
import {
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BadgeCheck,
  Banknote,
  Bell,
  Building2,
  Check,
  Circle,
  CreditCard,
  EllipsisVertical,
  FileText,
  House,
  Key,
  LogOut,
  Menu,
  MessageCircle,
  Minus,
  Moon,
  Package,
  Pencil,
  Plus,
  Search,
  ShoppingCart,
  Sun,
  Tag,
  Trash2,
  TrendingUp,
  User,
  Users,
  Wallet,
  Wifi,
  X,
} from "lucide-react";

interface Props {
  children?: ReactNode;
  fontSize?: "inherit" | "small" | "medium" | "large" | "default";
  color?: "inherit";
  className?: string;
  sx?: any;
  ownerState?: {
    size?: "small" | "medium" | "large";
  };
  [key: string]: any;
}

const iconMap: Record<string, any> = {
  home: House,
  search: Search,
  person: User,
  account_circle: User,
  key: Key,
  vpn_key: Key,
  close: X,
  menu: Menu,
  menu_open: Menu,
  dark_mode: Moon,
  light_mode: Sun,
  notifications: Bell,
  payment: CreditCard,
  paypal: Wallet,
  account_balance: Wallet,
  logout: LogOut,
  arrow_forward: ArrowRight,
  arrow_upward: ArrowUp,
  arrow_downward: ArrowDown,
  done: Check,
  remove: Minus,
  delete: Trash2,
  edit: Pencil,
  inventory_2: Package,
  shopping_cart: ShoppingCart,
  paid: Check,
  picture_as_pdf: FileText,
  priority_high: AlertTriangle,
  add: Plus,
  wifi: Wifi,
  donut_large: Circle,
  more_vert: EllipsisVertical,
  // dashboard icons
  group: Users,
  verified: BadgeCheck,
  payments: Banknote,
  chat: MessageCircle,
  // extra
  check_circle: Check,
  check_circle_outline: Check,
  done_all: Check,
  cancel: X,
  block: X,
  trending_up: TrendingUp,
  trending_down: TrendingUp,
  business: Building2,
  corporate_fare: Building2,
  domain: Building2,
  local_offer: Tag,
  tag: Tag,
  discount: Tag,
  error: AlertTriangle,
  warning: AlertTriangle,
  attach_money: Banknote,
  money: Banknote,
};

function resolveFontSize(fontSize?: Props["fontSize"]) {
  if (fontSize === "inherit") return "inherit";
  if (fontSize === "small") return "1.25rem";
  if (fontSize === "large") return "2.1875rem";
  return "1.5rem";
}

export default function SoftInputIconRoot({
  children,
  fontSize = "medium",
  sx,
  ownerState,
  ...rest
}: Props) {
  void ownerState;
  const iconName = typeof children === "string" ? children.trim() : "";
  const LucideIcon = iconMap[iconName];

  const iconNode = LucideIcon
    ? createElement(LucideIcon, { size: "1em" })
    : typeof children === "string"
    ? createElement(Circle, { size: "1em" })
    : children;

  return createElement(
    Box,
    {
      component: "span",
      sx: [
        {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
          fontSize: resolveFontSize(fontSize),
        },
        sx,
      ],
      ...rest,
    },
    iconNode
  );
}
